import React, { useMemo, memo } from "react";
import { JerseyNumberProps } from "./types";
import { sizeStyles, sizeArray, sizeMap as _sizeMap } from "shared/src/styles/sizeStyles";
import { cssColor } from "shared/src/utils/cssUtils";
import { useIdRef } from "shared/src/hooks/useIdRef";
import { getTeamInfo } from "shared/src/hooks/getTeamInfo";
import { processColor } from "shared/src/utils/colorUtils";

export const JerseyNumber = memo<JerseyNumberProps>(({
  value,
  size = 16,
  strokeOutside = "silver-800",
  strokeInside = "silver-1100",
  fill = "linear-gradient(180deg, rgba(255, 255, 255, 1) 100%, rgba(0, 0, 0, 0.20) 100%)",
  className = "",
  teamInfo, 
  team,
  colorType = "normal",
  ...rest
}: JerseyNumberProps) => {
  console.log({strokeOutside, procesedOutside: processColor(strokeOutside, "dark"), strokeInside, processedInside: processColor(strokeInside, "dark")})
  const _id = useIdRef({ str: "cui-jersey-number-" }).current;
  const { _size } = useMemo(()=>{
    const [isStr, isNum] = [typeof size === "string", typeof size === "number"];
    if (isStr || isNum) {
      const isSemanticSize = isStr && size && sizeArray.includes(size as string);
      if (isSemanticSize) {
        const mappedSize = _sizeMap.get(size);
        return {_size: mappedSize.width }
      }
      if (isStr && (size as string).includes("px")) {
        return {_size: size }
      }
      if (isNum) {
        return {_size: size }
      }
    }
    return { _size: 16 }
  },[size]);

  const _teamInfo = useMemo(()=>{
    if (teamInfo) return teamInfo;
    if (team && typeof team === "string") return getTeamInfo(team);
    return null; 
  },[teamInfo, team])

  const _value = useMemo(() => {
    if (typeof value !== "string" && typeof value !== "number") return null;
    return typeof value === "string" ? value : value.toString();
  }, [value]);

  const { sizeMap } = useMemo(() => {
    return sizeStyles(size);
  }, [size]);

  const { _strokeOutside, _strokeInside, _fill } =
    useMemo(() => {
      return {
        _strokeOutside: processColor(cssColor(strokeOutside), colorType),
        _strokeInside: cssColor(strokeInside),
        _fill: cssColor(fill),
      };
    }, [strokeOutside, strokeInside, fill, colorType]);

  const ids = useMemo(() => {
    return {
      mainText: `${_id}-main-text`,
      defaultGradient: `${_id}-default-fill`,
      innerMask: `${_id}-inner-mask`,
      outerMask: `${_id}-outer-mask`,
      textClip: `${_id}-text-clip`,
      svg: `${_id}-svg`,
    };
  }, []);

  const weight = 1;
  const computation = ((_size as number)/ 16) * weight;
  const computedScale = !Number.isNaN(computation) ? computation : 1;

  const baseline = 16;
  return (
    <svg
      viewBox={`0 0 ${baseline+1} ${baseline+2}`}
      {...rest}
      id={ids.svg}
      className={`cui__jersey-number ${className}`}
      style={{
        minWidth: baseline,
        minHeight: baseline,
        height: "auto",
        width: "auto",
        maxWidth: baseline,
        overflow: "hidden",
        transform: `scale(${computedScale})`
      }}
    >
      <defs>
        {/* Main text definition */}
        <text
          id={ids.mainText}
          x={0}
          y={baseline}
          fontFamily="College"
          fontWeight="bold"
          fontSize={baseline}
          letterSpacing={"-4%"}
        >
          {_value}
        </text>

        {/* Gradient fill for top text */}
        <linearGradient
          id={ids.defaultGradient}
          x1="0%"
          y1="0%"
          x2="0%"
          y2="100%"
        >
          <stop offset="0%" stopColor="white" stopOpacity="1" />
          <stop offset="30%" stopColor="white" stopOpacity="0.5" />
          <stop offset="100%" stopColor="black" stopOpacity="0.2" />
        </linearGradient>

        {/* ClipPath for text shape */}
        <clipPath id={ids.textClip}>
          <use href={`#${ids.mainText}`} />
        </clipPath>
      </defs>

      {/* Outer stroke (behind everything) */}
      <text
        x={0}
        y={baseline}
        fontFamily="College"
        fontWeight="bold"
        fontSize={baseline}
        letterSpacing={"-4%"}
        strokeWidth={2}
        stroke={_strokeOutside}
      >
        {_value}
      </text>
      {/* Gradient-filled text on top */}
      <text
        x={0}
        y={baseline}
        fontFamily="College"
        fontWeight="bold"
        fontSize={baseline}
        letterSpacing={"-4%"}
        fill="white"
      >
        {_value}
      </text>
      <use
        href={`#${ids.mainText}`}
        fill={`url(#${ids.defaultGradient})`}
        opacity="1"
        stroke={_strokeInside}
        strokeWidth={.5}
        clipPath={`url(#${ids.textClip})`}
      />
    </svg>
  );
});
